import { useMemo, useState } from "react";
import { Box, Button, Checkbox, Chip, CircularProgress, Radio, Tab, Tabs, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import useLogisticsTicketVendors from "hooks/useLogisticsTicketVendors";
import { JobDetailAssignment, JobDetailViewModel } from "./jobDetailTypes";
import { DetailCard } from "./DetailCard";
import RequestQuoteConfirmationModal, { DirectAssignDetails } from "./RequestQuoteConfirmationModal";
import VendorQuoteResponsesSection from "./VendorQuoteResponsesSection";
import VendorContactBlock from "./VendorContactBlock";
import VendorContactModal from "./VendorContactModal";
import { buildVendorQuoteRows, VendorQuoteRow } from "./vendorQuoteUtils";
import { EventType, Timeline } from "./Timeline";

type Props = {
  job: JobDetailViewModel;
  onQuotesRequested?: () => void;
};

const QUOTE_REQUEST_STAGES = new Set(["new_orders", "pending_confirmation"]);

type ConfirmMode = "assign" | "quotes" | "assignFromQuote" | null;

function VendorAvatar({ vendor }: { vendor: Pick<VendorQuoteRow, "color" | "initial"> }) {
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "8px",
        backgroundColor: vendor.color,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: 14,
        flexShrink: 0,
      }}
    >
      {vendor.initial}
    </Box>
  );
}

function VendorRow({
  vendor,
  selected,
  disabled,
  control,
  onToggle,
}: {
  vendor: VendorQuoteRow;
  selected: boolean;
  disabled: boolean;
  control: "checkbox" | "radio";
  onToggle: () => void;
}) {
  const isSent = Boolean(vendor.sentAt);

  return (
    <Box
      onClick={() => !disabled && !isSent && onToggle()}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        borderRadius: "8px",
        border: "1px solid",
        borderColor: selected ? "primary.main" : "#ECECEC",
        backgroundColor: selected ? "#F5F9FF" : "background.paper",
        cursor: !disabled && !isSent ? "pointer" : "default",
        opacity: isSent ? 0.55 : 1,
      }}
    >
      {control === "checkbox" ? (
        <Checkbox
          checked={selected}
          disabled={disabled || isSent}
          size="small"
          sx={{ p: 0.5 }}
          onClick={(event) => event.stopPropagation()}
          onChange={onToggle}
        />
      ) : (
        <Radio
          checked={selected}
          disabled={disabled || isSent}
          size="small"
          sx={{ p: 0.5 }}
          onClick={(event) => event.stopPropagation()}
          onChange={onToggle}
        />
      )}
      <VendorAvatar vendor={vendor} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography fontWeight={600} sx={{ fontSize: 14 }}>
            {vendor.name}
          </Typography>
          {isSent && (
            <Chip
              label="SENT"
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.5,
                backgroundColor: "#E3F2FD",
                color: "#1565C0",
              }}
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
          {[vendor.vendorType, vendor.email].filter(Boolean).join(" · ")}
        </Typography>
      </Box>
      <Typography fontWeight={700} sx={{ fontSize: 15, flexShrink: 0 }}>
        {vendor.priceLabel}
      </Typography>
    </Box>
  );
}

function CurrentAssignmentCard({
  assignment,
  requestedDate,
}: {
  assignment: JobDetailAssignment;
  requestedDate: string;
}) {
  const [editingPrimaryContact, setEditingPrimaryContact] = useState(false);
  const [primaryContact, setPrimaryContact] = useState({
    contactName: assignment.vendorContactName,
    email: assignment.vendorEmail,
    phone: assignment.vendorPhone,
  });

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1.5, fontWeight: 700, letterSpacing: 0.8 }}
      >
        CURRENT ASSIGNMENT
      </Typography>

      <Box
        sx={{
          p: 2,
          borderRadius: "8px",
          border: "1px solid #C8E6C9",
          backgroundColor: "#F7FBF7",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <VendorAvatar vendor={assignment} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.25 }}>
              <Typography fontWeight={700} sx={{ fontSize: 15 }}>
                {assignment.vendorName}
              </Typography>
              <Chip
                label="ASSIGNED"
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  backgroundColor: "#E8F5E9",
                  color: "#2E7D32",
                }}
              />
              {assignment.proposedDate && assignment.proposedDate != requestedDate && (
                <Chip color="warning" label={`Proposed: ${assignment.proposedDate}`} size="small" />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
              {assignment.vendorType}
            </Typography>
          </Box>
          <Typography fontWeight={700} sx={{ fontSize: 16, flexShrink: 0 }}>
            {assignment.priceLabel}
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: "1px solid #E0E0E0",
          }}
        >
          <VendorContactBlock
            title="PRIMARY CONTACT"
            name={primaryContact.contactName}
            email={primaryContact.email}
            phone={primaryContact.phone}
            onEdit={() => setEditingPrimaryContact(true)}
          />
        </Box>
      </Box>

      <Box sx={{ my: 2.5 }}>
        <Timeline
          events={[
            {
              eventName: assignment.vendorAcceptedAt ? "Vendor Confirmed" : "Awaiting Vendor Confirmation",
              date: assignment.vendorAcceptedAt,
              type: assignment.vendorAcceptedAt ? EventType.CONFIRMED : EventType.AWAITING,
              url: assignment.vendorPortalUrl,
            },
            {
              eventName: assignment.customerConfirmedAt
                ? "Customer Confirmed"
                : assignment.vendorAcceptedAt
                  ? "Awaiting Customer Confirmation"
                  : "",
              date: assignment.customerConfirmedAt,
              type: assignment.customerConfirmedAt
                ? EventType.CONFIRMED
                : assignment.vendorAcceptedAt
                  ? EventType.AWAITING
                  : EventType.NO_EVENT,
              url: assignment.customerPortalUrl,
            },
          ]}
        />
      </Box>

      <VendorContactModal
        open={editingPrimaryContact}
        setOpen={setEditingPrimaryContact}
        vendorUuid={assignment.vendorId}
        vendorName={assignment.vendorName}
        contactName={primaryContact.contactName}
        email={primaryContact.email}
        phone={primaryContact.phone}
        onSaved={(vendor) =>
          setPrimaryContact({
            contactName: vendor.contactName,
            email: vendor.email,
            phone: vendor.phone,
          })
        }
      />
    </Box>
  );
}

export default function VendorManagementCard({ job, onQuotesRequested }: Props) {
  const [tab, setTab] = useState(0);
  const [assignVendorId, setAssignVendorId] = useState<string | null>(null);
  const [quoteVendorIds, setQuoteVendorIds] = useState<string[]>([]);
  const [quoteAssignVendorId, setQuoteAssignVendorId] = useState<string | null>(null);
  const [confirmMode, setConfirmMode] = useState<ConfirmMode>(null);
  const { vendors, loading, submitting, requestQuotes } = useLogisticsTicketVendors(job.ticketUuid);

  const vendorRows = useMemo(() => buildVendorQuoteRows(vendors), [vendors]);
  const assignedVendor = useMemo(() => {
    const fromVendors = vendorRows.find((vendor) => vendor.isAssigned);
    if (fromVendors) {
      return fromVendors;
    }

    if (!job.currentAssignment) {
      return null;
    }

    return (
      vendorRows.find((vendor) => vendor.id === job.currentAssignment?.vendorId) ?? {
        id: job.currentAssignment.vendorId,
        name: job.currentAssignment.vendorName,
        contactName: job.currentAssignment.vendorContactName,
        email: job.currentAssignment.vendorEmail,
        phone: job.currentAssignment.vendorPhone,
        vendorType: job.currentAssignment.vendorType,
        priceLabel: job.currentAssignment.priceLabel,
        priceInCents: job.currentAssignment.priceInCents,
        quoteAmountInCents: job.currentAssignment.priceInCents,
        initial: job.currentAssignment.initial,
        color: job.currentAssignment.color,
        hasQuote: true,
        isDeclined: false,
        isAssigned: true,
        responseStatus: "awaiting" as const,
      }
    );
  }, [job.currentAssignment, vendorRows]);

  const currentAssignment = useMemo<JobDetailAssignment | null>(() => {
    if (job.currentAssignment) {
      return job.currentAssignment;
    }

    if (!assignedVendor) {
      return null;
    }

    return {
      vendorId: assignedVendor.id,
      vendorName: assignedVendor.name,
      vendorContactName: assignedVendor.contactName,
      vendorEmail: assignedVendor.email,
      vendorPhone: assignedVendor.phone,
      vendorType: assignedVendor.vendorType,
      priceLabel: assignedVendor.priceLabel,
      priceInCents: assignedVendor.priceInCents,
      initial: assignedVendor.initial,
      color: assignedVendor.color,
    };
  }, [assignedVendor, job.currentAssignment]);

  const responseVendors = useMemo(
    () => vendorRows.filter((vendor) => vendor.sentAt && !vendor.isAssigned),
    [vendorRows],
  );
  const assignableVendors = useMemo(
    () => vendorRows.filter((vendor) => !vendor.sentAt && !vendor.isAssigned),
    [vendorRows],
  );

  const hasDestination = job.destinationAddressId != null;
  const canRequestVendors = QUOTE_REQUEST_STAGES.has(job.stage) && !currentAssignment;
  const canManageVendors = hasDestination && canRequestVendors && !submitting;
  const quotesTabLabel = responseVendors.length > 0 ? `Vendor Quotes (${responseVendors.length})` : "Vendor Quotes";

  const assignVendor = useMemo(
    () => vendorRows.find((vendor) => vendor.id === assignVendorId && !vendor.sentAt) ?? null,
    [vendorRows, assignVendorId],
  );
  const selectedQuoteVendors = useMemo(
    () => vendorRows.filter((vendor) => quoteVendorIds.includes(vendor.id) && !vendor.sentAt),
    [vendorRows, quoteVendorIds],
  );
  const quoteAssignVendor = useMemo(
    () => vendorRows.find((vendor) => vendor.id === quoteAssignVendorId) ?? null,
    [vendorRows, quoteAssignVendorId],
  );

  const toggleQuoteVendor = (vendorId: string) => {
    const vendor = vendorRows.find((row) => row.id === vendorId);
    if (!vendor || vendor.sentAt) {
      return;
    }

    setQuoteVendorIds((current) =>
      current.includes(vendorId) ? current.filter((id) => id !== vendorId) : [...current, vendorId],
    );
  };

  const handleConfirm = (details?: DirectAssignDetails) => {
    if ((confirmMode === "assign" || confirmMode === "assignFromQuote") && (assignVendor || quoteAssignVendor)) {
      const vendor = confirmMode === "assign" ? assignVendor : quoteAssignVendor;
      if (!vendor || !details) {
        return Promise.resolve();
      }

      return requestQuotes([vendor.id], {
        assignmentType: "immediate",
        amountInCents: details.amountInCents,
        scheduledAt: details.scheduledAt,
        confirmedTime: details.confirmedTime,
      }).then(() => {
        setAssignVendorId(null);
        setQuoteAssignVendorId(null);
        onQuotesRequested?.();
      });
    }

    if (confirmMode === "quotes" && selectedQuoteVendors.length > 0) {
      return requestQuotes(
        selectedQuoteVendors.map((vendor) => vendor.id),
        { assignmentType: "request" },
      ).then(() => {
        setQuoteVendorIds([]);
        onQuotesRequested?.();
      });
    }

    return Promise.resolve();
  };

  const modalVendors =
    confirmMode === "assign"
      ? assignVendor
        ? [assignVendor]
        : []
      : confirmMode === "assignFromQuote"
        ? quoteAssignVendor
          ? [quoteAssignVendor]
          : []
        : selectedQuoteVendors;

  return (
    <DetailCard title="Vendor Management">
      {canRequestVendors && !hasDestination && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Set a Revivn destination before requesting quotes or assigning a vendor.
        </Typography>
      )}
      <Tabs
        value={tab}
        onChange={(_event, value) => setTab(value)}
        sx={{
          minHeight: 36,
          mb: 2,
          borderBottom: "1px solid #E0E0E0",
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 36, px: 0, mr: 3 },
        }}
      >
        <Tab label="Assign Vendor" />
        <Tab label={quotesTabLabel} />
      </Tabs>

      {tab === 0 && (
        <Box>
          {currentAssignment ? (
            <CurrentAssignmentCard assignment={currentAssignment} requestedDate={job.pickupDate} />
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Assign a vendor directly to this pickup. They will be marked confirmed and emailed a pickup summary.
              </Typography>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : assignableVendors.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {vendorRows.length === 0
                    ? "No vendors available."
                    : "All vendors already have a request sent for this pickup. Use Vendor Quotes to assign a responding vendor."}
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {assignableVendors.map((vendor) => (
                    <VendorRow
                      key={vendor.id}
                      vendor={vendor}
                      selected={assignVendorId === vendor.id}
                      disabled={!canManageVendors}
                      control="radio"
                      onToggle={() => setAssignVendorId(vendor.id)}
                    />
                  ))}
                </Box>
              )}

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2.5, gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                  {assignVendor ? `Selected: ${assignVendor.name}` : "Select one vendor to assign"}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddAlt1Icon />}
                  disabled={!canManageVendors || !assignVendor || loading}
                  onClick={() => setConfirmMode("assign")}
                  sx={{ textTransform: "uppercase", fontWeight: 700 }}
                >
                  Assign Vendor
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <VendorQuoteResponsesSection
                vendors={responseVendors}
                canAssign={!currentAssignment && canManageVendors}
                onAssignVendor={(vendorId) => {
                  setQuoteAssignVendorId(vendorId);
                  setConfirmMode("assignFromQuote");
                }}
                requestedDate={job.pickupDate}
              />

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 2, fontWeight: 700, letterSpacing: 0.8 }}
              >
                SELECT VENDORS TO REQUEST QUOTES
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {vendorRows.map((vendor) => (
                  <VendorRow
                    key={vendor.id}
                    vendor={vendor}
                    selected={quoteVendorIds.includes(vendor.id)}
                    disabled={!canManageVendors}
                    control="checkbox"
                    onToggle={() => toggleQuoteVendor(vendor.id)}
                  />
                ))}
              </Box>
            </>
          )}

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2.5, gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
              {selectedQuoteVendors.length > 0
                ? `${selectedQuoteVendors.length} vendor${selectedQuoteVendors.length === 1 ? "" : "s"} selected`
                : "Select vendors above to send quote requests"}
            </Typography>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              disabled={!canManageVendors || selectedQuoteVendors.length === 0 || loading}
              onClick={() => setConfirmMode("quotes")}
              sx={{ textTransform: "uppercase", fontWeight: 700 }}
            >
              Request Quotes
            </Button>
          </Box>
        </Box>
      )}

      <RequestQuoteConfirmationModal
        open={confirmMode !== null}
        setOpen={(open) => {
          if (!open) {
            setConfirmMode(null);
            setQuoteAssignVendorId(null);
          }
        }}
        job={job}
        selectedVendors={modalVendors}
        onConfirm={handleConfirm}
        submitting={submitting}
        directAssign={confirmMode === "assign" || confirmMode === "assignFromQuote"}
      />
    </DetailCard>
  );
}
